<template>
  <div class="welcome-screen">
    <div class="welcome-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading kiosk settings...</p>
      </div>
      
      <!-- Main Content (shown after loading, even on API error) -->
      <template v-else>
        <h1 class="welcome-title">{{ kioskTitle }}</h1>
        <p class="welcome-message">{{ welcomeMessage }}</p>
        
        <div class="welcome-cta">
          <button 
            class="btn btn-primary btn-lg start-btn" 
            @click="startTransaction"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isLoading ? 'Loading...' : 'Touch to Start' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KioskWelcome',
  props: {
    config: {
      type: Object,
      default: () => ({
        showLogo: true,
        animationEnabled: true
      })
    },
    transactionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      kioskTitle: 'Welcome to Hercor College',
      welcomeMessage: 'Express Transaction Queueing System',
      isLoading: true,
      error: null
    };
  },
  async created() {
    // Reset the kiosk state when landing on welcome page
    if (this.$store && this.$store.dispatch) {
      this.$store.dispatch('kiosk/resetKiosk');
    }

    await this.fetchKioskSettings();
  },
  
  methods: {
    async fetchKioskSettings() {
      this.isLoading = true;
      
      try {
        const timeoutMs = 2000;

        const apiCall = (async () => {
          // Import settingsApi directly
          const { settingsApi } = await import('@/services/api');
          // Fetch kiosk settings
          return settingsApi.getKioskSettings();
        })();

        const timeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Kiosk settings request timed out')), timeoutMs);
        });

        const response = await Promise.race([apiCall, timeout]);

        if (response && response.data) {
          // Update the data properties with the fetched values
          this.kioskTitle = response.data.kiosk_name || this.kioskTitle;
          this.welcomeMessage = response.data.welcome_message || this.welcomeMessage;
          
          // Also save to local storage for offline use
          localStorage.setItem('cachedKioskTitle', this.kioskTitle);
          localStorage.setItem('cachedWelcomeMessage', this.welcomeMessage);
        }
      } catch (error) {
        console.error('Error fetching kiosk settings:', error);
        
        // Fallback to cached values if available
        const cachedTitle = localStorage.getItem('cachedKioskTitle');
        const cachedMessage = localStorage.getItem('cachedWelcomeMessage');
        
        // Use cached values or hardcoded defaults
        this.kioskTitle = cachedTitle || 'Welcome to Hercor College';
        this.welcomeMessage = cachedMessage || 'Express Transaction Queueing System';
        
        // Save fallback values to localStorage for next time
        if (!cachedTitle) localStorage.setItem('cachedKioskTitle', this.kioskTitle);
        if (!cachedMessage) localStorage.setItem('cachedWelcomeMessage', this.welcomeMessage);
      } finally {
        this.isLoading = false;
      }
    },
    startTransaction() {
      // Emit step-complete event to move to the next step in the flow
      this.$emit('step-complete', {});
    }
  }
};
</script>

<style scoped>
.welcome-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  text-align: center;
  padding: 2rem;
  position: relative;
  background: linear-gradient(160deg, var(--brand-ice) 0%, var(--brand-surface) 40%, #ffffff 100%);
  font-family: var(--font-body);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: 200px;
}

.loading-state .spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.25em;
}

.welcome-content {
  max-width: 700px;
}

.welcome-title {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 3rem;
  margin-bottom: 1.25rem;
  color: var(--brand-dark);
}

.welcome-message {
  font-family: var(--font-body);
  font-size: 1.5rem;
  margin-bottom: 3rem;
  color: var(--brand-primary);
}

.welcome-cta {
  margin-top: 2rem;
}

.start-btn {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 1.8rem;
  padding: 1.1rem 3.5rem;
  border-radius: 12px;
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
  box-shadow: 0 6px 18px rgba(24, 149, 167, 0.35);
  transition: all 0.3s ease;
}

.start-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 22px rgba(24, 149, 167, 0.4);
  background-color: var(--brand-cyan);
  border-color: var(--brand-cyan);
  outline: 3px solid var(--brand-accent);
  outline-offset: 2px;
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .welcome-title {
    font-size: 3.5rem;
  }
  
  .welcome-message {
    font-size: 1.9rem;
  }
  
  .start-btn {
    font-size: 2.2rem;
    padding: 1.5rem 4rem;
  }
}
</style> 